import React, { useMemo } from "react";
import ExplorePost from "./ExplorePost";
import api from "@/config/axiosConfig";
import { useQuery } from "@tanstack/react-query";
import { ExploreTrip, ExploreResponse } from "@/types/social";
import { InboxIcon } from "@heroicons/react/24/outline";
import { useToast } from "@/hooks/use-toast";
import useLodgeStore from "@/stores/lodge/lodgeStore";
import useAuthStore from "@/stores/user/authStore";

const getExplorePosts = async (): Promise<ExploreResponse> => {
  try {
    const response = await api.social.get<ExploreResponse>("/explore");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch explore posts:", error);
    throw error;
  }
};

const ExplorePostSkeleton = React.memo(() => {
  return (
    <div className="border rounded-xl p-3 bg-white h-full w-full animate-pulse">
      {/* Header Skeleton */}
      <div className="flex justify-between items-center mb-4">
        <div className="h-5 w-1/3 bg-gray-200 rounded"></div>
        <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
      </div>

      {/* User Info Skeleton */}
      <div className="flex items-center space-x-2 mb-4">
        <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
        <div className="h-3 w-24 bg-gray-200 rounded"></div>
      </div>

      {/* Tags Skeleton */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        <div className="h-5 w-20 bg-gray-200 rounded-lg"></div>
        <div className="h-5 w-16 bg-gray-200 rounded-lg"></div>
        <div className="h-5 w-24 bg-gray-200 rounded-lg"></div>
      </div>

      {/* Description Skeleton */}
      <div className="space-y-2 mb-4">
        <div className="h-3 w-full bg-gray-200 rounded"></div>
        <div className="h-3 w-5/6 bg-gray-200 rounded"></div>
        <div className="h-3 w-4/6 bg-gray-200 rounded"></div>
      </div>

      {/* Images Grid Skeleton */}
      <div className="w-full h-56 grid grid-cols-3 gap-2 mb-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-gray-200 rounded-lg"></div>
        ))}
      </div>

      {/* Engagement Skeleton */}
      <div className="flex items-center gap-4 mt-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 w-16 bg-gray-200 rounded-full"></div>
        ))}
      </div>
    </div>
  );
});

ExplorePostSkeleton.displayName = "ExplorePostSkeleton";

const EmptyState = React.memo(() => (
  <div className="flex flex-col items-center justify-center h-full">
    <div className="bg-neutral-50 rounded-full p-4 mb-4">
      <InboxIcon className="w-8 h-8 text-neutral-500" />
    </div>
    <h3 className="text-lg font-medium text-neutral-900 mb-2">
      No trips found
    </h3>
    <p className="text-neutral-500 text-center text-sm max-w-xs font-light">
      Start planning a trip or follow other travelers to see their trips here.
    </p>
  </div>
));

EmptyState.displayName = "EmptyState";

const ExploreFeed: React.FC = () => {
  const { toast } = useToast();
  const anonymousUser = useAuthStore((state) => state.anonymousUser);
  const { setFriends } = useLodgeStore();
  const { data, isLoading, error } = useQuery<ExploreResponse>({
    queryKey: ["explorePosts"],
    queryFn: getExplorePosts,
  });

  const sortedPosts = useMemo(() => {
    if (!data) return [];
    return data.trips.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [data]);

  if (error) {
    toast({
      title: "Error fetching explore posts",
      description: error.message,
      variant: "destructive",
    });
    return (
      <div className="text-red-500 text-center py-4">
        Failed to load explore posts. Please try again later.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 w-full">
        {[1, 2, 3].map((i) => (
          <ExplorePostSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (sortedPosts.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4 w-full 2xl:text-lg">
      {sortedPosts.map((post: ExploreTrip) => (
        <ExplorePost key={post.id} post={post} anonymousUser={anonymousUser} />
      ))}
    </div>
  );
};

export default ExploreFeed;
